import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import {
  Admin,
  AdminApiResponse,
  Status,
  USER_URL,
  UserTypes,
} from '../shared/src';
import { ErrorHandlerService } from './error-handler.service';

@Injectable({
  providedIn: 'root',
})
export class AdminService {
  private readonly ADMIN_API = `${USER_URL}/admin`;
  private readonly MASTERADMIN_API = `${USER_URL}`;
  constructor(
    private http: HttpClient,
    private errorHandler: ErrorHandlerService
  ) {}

  private handleError(error: HttpErrorResponse) {
    if (error.status === 0) {
    } else {
    }
    return throwError(() => '');
  }

  /**
   * Sends a POST request to the server to register admin information.
   * @param admin - The  admin data to be added
   * @returns
   */
  public registerMasterAdmin(admin: Admin): Observable<Admin> {
    return this.http
      .post<Admin>(`${this.ADMIN_API}/register`, admin)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * api for get admin Info
   * @param params
   * @returns
   */
  getAdminList(params: {
    pageIndex: number;
    pageSize: number;
    searchFilter?: string;
    roleId: number | number[];
  }): Observable<AdminApiResponse> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);

    // Check if roleId is an array or a single value
    if (Array.isArray(params.roleId)) {
      params.roleId.forEach((id) => {
        queryParams = queryParams.append('roleId', id.toString()); // Append each roleId
      });
    } else {
      queryParams = queryParams.set('roleId', params.roleId.toString()); // Set single roleId
    }

    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }
    return this.http
      .get<AdminApiResponse>(`${this.ADMIN_API}/getAllAdmins`, {
        params: queryParams,
      })
      .pipe(catchError(this.handleError));
  }
  public getAdminsUserTypes(): Observable<UserTypes> {
    return this.http
      .get<UserTypes>(`${this.ADMIN_API}/admin-roles`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  //get All role data
  public getAllRole(): Observable<any> {
    return this.http
      .get<any>(`${this.MASTERADMIN_API}/role/getAll`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public addAdmin(data: Admin): Observable<Admin> {
    return this.http
      .post<Admin>(`${this.ADMIN_API}/register`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Updates the active status of a admin.
   *
   * @param adminStatus - The object containing the admin's ID and the new block status to be updated.
   * @returns An Observable of the updated admin object.
   */

  public blockAdmin(adminStatus: Status): Observable<Admin> {
    return this.http
      .put<Admin>(
        `${this.ADMIN_API}/updateActiveStatus/${adminStatus.id}`,
        adminStatus
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Updates master information on the server.
   * @param id - The unique identifier of the master to be updated.
   * @param data - The master data to be updated.
   * @returns An Observable that emits the updated Master object.
   */
  public updateMasterAdminInfo(id: number, data: Admin): Observable<Admin> {
    return this.http
      .put<Admin>(`${this.ADMIN_API}/update-admin/${id}`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Deletes a admin from the server.
   * @param id - The unique identifier of the admin to be deleted.
   * @returns An Observable that emits a confirmation message upon successful deletion.
   */
  public deleteAdmin(id: number): Observable<string> {
    return this.http
      .delete<string>(`${this.ADMIN_API}/deleteAdmins/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Imports a list of admins into the server.
   * @param data - An array of Admins objects to be imported.
   * @returns An Observable that emits an array of imported Admins objects upon successful import.
   */
  public importAdmins(data: Admin[]): Observable<Admin[]> {
    return this.http
      .post<Admin[]>(`${this.ADMIN_API}/importAdmins`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }
}
